import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiError = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

const EMPTY_CONTACT = { department: "", name: "", title: "", email: "", phone: "", notes: "" };
const EMPTY_KNOWLEDGE = { topic: "", content: "" };

const inputClass =
    "w-full border border-gray-300 bg-white px-3.5 py-2.5 text-[14px] text-ink placeholder:text-gray-400 focus:border-gold focus:outline-none";

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem("pe_admin_token") || "");
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [loginError, setLoginError] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [tab, setTab] = useState("contacts");
    const [contacts, setContacts] = useState([]);
    const [knowledge, setKnowledge] = useState([]);
    const [editingContact, setEditingContact] = useState(null);
    const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
    const [editingKnowledge, setEditingKnowledge] = useState(null);
    const [knowledgeForm, setKnowledgeForm] = useState(EMPTY_KNOWLEDGE);

    useEffect(() => {
        let el = document.head.querySelector('meta[name="robots"]');
        if (!el) {
            el = document.createElement("meta");
            el.setAttribute("name", "robots");
            document.head.appendChild(el);
        }
        el.setAttribute("content", "noindex, nofollow");
        document.title = "Owner Console | Pacific Expressway";
        return () => el.setAttribute("content", "index, follow");
    }, []);

    const authed = axios.create({
        baseURL: API,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    const loadData = async () => {
        try {
            const [c, k] = await Promise.all([
                authed.get("/admin/contacts"),
                authed.get("/admin/knowledge"),
            ]);
            setContacts(c.data.items);
            setKnowledge(k.data.items);
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem("pe_admin_token");
                setToken("");
            } else {
                toast.error("Failed to load data.");
            }
        }
    };

    useEffect(() => {
        if (token) loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (loggingIn) return;
        setLoggingIn(true);
        setLoginError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, {
                email: loginForm.email.trim(),
                password: loginForm.password,
            });
            localStorage.setItem("pe_admin_token", data.token);
            setToken(data.token);
        } catch (err) {
            setLoginError(formatApiError(err.response?.data?.detail));
        } finally {
            setLoggingIn(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("pe_admin_token");
        setToken("");
    };

    const saveContact = async (e) => {
        e.preventDefault();
        const payload = Object.fromEntries(
            Object.entries(contactForm).map(([k, v]) => [k, v.trim() || null])
        );
        payload.department = contactForm.department.trim();
        try {
            if (editingContact) {
                await authed.put(`/admin/contacts/${editingContact}`, payload);
                toast.success("Contact updated.");
            } else {
                await authed.post("/admin/contacts", payload);
                toast.success("Contact added.");
            }
            setContactForm(EMPTY_CONTACT);
            setEditingContact(null);
            loadData();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const removeContact = async (id) => {
        try {
            await authed.delete(`/admin/contacts/${id}`);
            toast.success("Contact removed.");
            loadData();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const saveKnowledge = async (e) => {
        e.preventDefault();
        try {
            if (editingKnowledge) {
                await authed.put(`/admin/knowledge/${editingKnowledge}`, {
                    topic: knowledgeForm.topic.trim(),
                    content: knowledgeForm.content.trim(),
                });
                toast.success("Answer updated.");
            } else {
                await authed.post("/admin/knowledge", {
                    topic: knowledgeForm.topic.trim(),
                    content: knowledgeForm.content.trim(),
                });
                toast.success("Answer added.");
            }
            setKnowledgeForm(EMPTY_KNOWLEDGE);
            setEditingKnowledge(null);
            loadData();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const removeKnowledge = async (id) => {
        try {
            await authed.delete(`/admin/knowledge/${id}`);
            toast.success("Answer removed.");
            loadData();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    if (!token) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-ink px-5 pt-20">
                <form
                    onSubmit={handleLogin}
                    data-testid="admin-login-form"
                    className="w-full max-w-sm bg-white p-8 border border-gray-200"
                >
                    <img src="/logo.png" alt="Pacific Expressway" className="h-12 w-auto" />
                    <h1 className="mt-6 font-heading text-xl font-bold text-ink">Owner Console</h1>
                    <p className="mt-1 text-sm text-gray-500">Authorized access only.</p>
                    {loginError && (
                        <p role="alert" data-testid="admin-login-error" className="mt-4 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {loginError}
                        </p>
                    )}
                    <label className="mt-6 block text-xs font-bold uppercase tracking-[0.15em] text-ink" htmlFor="admin-email">
                        Email
                    </label>
                    <input
                        id="admin-email"
                        data-testid="admin-login-email"
                        type="email"
                        autoComplete="username"
                        required
                        value={loginForm.email}
                        onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
                        className={`${inputClass} mt-2`}
                    />
                    <label className="mt-4 block text-xs font-bold uppercase tracking-[0.15em] text-ink" htmlFor="admin-password">
                        Password
                    </label>
                    <input
                        id="admin-password"
                        data-testid="admin-login-password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={loginForm.password}
                        onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                        className={`${inputClass} mt-2`}
                    />
                    <button
                        type="submit"
                        data-testid="admin-login-submit"
                        disabled={loggingIn}
                        className="mt-6 w-full bg-gold px-6 py-3.5 font-heading text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-gold-light disabled:opacity-60"
                    >
                        {loggingIn ? "Signing in…" : "Sign In"}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-mist px-5 pb-20 pt-28 sm:px-8">
            <div className="mx-auto max-w-4xl">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink" data-testid="admin-title">
                            Owner Console
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Everything here feeds the website chat assistant.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={logout}
                        data-testid="admin-logout-button"
                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-gold hover:text-gold"
                    >
                        <LogOut size={15} /> Sign Out
                    </button>
                </div>

                <div className="mt-8 flex gap-2 border-b border-gray-300">
                    {[
                        { key: "contacts", label: "Contact Directory", testid: "admin-tab-contacts" },
                        { key: "knowledge", label: "Chat Q&A", testid: "admin-tab-knowledge" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            data-testid={t.testid}
                            onClick={() => setTab(t.key)}
                            className={`px-5 py-3 font-heading text-sm font-bold uppercase tracking-[0.1em] transition-colors ${
                                tab === t.key
                                    ? "border-b-2 border-gold text-ink"
                                    : "text-gray-500 hover:text-ink"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "contacts" && (
                    <div className="mt-8 grid gap-8">
                        <form onSubmit={saveContact} data-testid="admin-contact-form" className="bg-white p-6 border border-gray-200">
                            <h2 className="font-heading text-lg font-bold text-ink">
                                {editingContact ? "Edit Contact" : "Add Contact"}
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                e.g. Department: Accounts Payable — the assistant will route people here.
                            </p>
                            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                <input aria-label="Department" placeholder="Department * (e.g. Accounts Receivable)" required value={contactForm.department}
                                    onChange={(e) => setContactForm((f) => ({ ...f, department: e.target.value }))}
                                    data-testid="admin-contact-department" className={inputClass} />
                                <input aria-label="Name" placeholder="Name (e.g. Maria Lopez)" value={contactForm.name}
                                    onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
                                    data-testid="admin-contact-name" className={inputClass} />
                                <input aria-label="Title" placeholder="Title (e.g. AP Manager)" value={contactForm.title}
                                    onChange={(e) => setContactForm((f) => ({ ...f, title: e.target.value }))}
                                    data-testid="admin-contact-title" className={inputClass} />
                                <input aria-label="Email" placeholder="Email" value={contactForm.email}
                                    onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                                    data-testid="admin-contact-email" className={inputClass} />
                                <input aria-label="Phone" placeholder="Phone" value={contactForm.phone}
                                    onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))}
                                    data-testid="admin-contact-phone" className={inputClass} />
                                <input aria-label="Notes" placeholder="Notes (when to route here)" value={contactForm.notes}
                                    onChange={(e) => setContactForm((f) => ({ ...f, notes: e.target.value }))}
                                    data-testid="admin-contact-notes" className={inputClass} />
                            </div>
                            <div className="mt-5 flex gap-3">
                                <button type="submit" data-testid="admin-contact-save"
                                    className="inline-flex items-center gap-2 bg-gold px-6 py-3 font-heading text-xs font-bold uppercase tracking-[0.14em] text-ink hover:bg-gold-light">
                                    <Plus size={14} /> {editingContact ? "Save Changes" : "Add Contact"}
                                </button>
                                {editingContact && (
                                    <button type="button" data-testid="admin-contact-cancel"
                                        onClick={() => { setEditingContact(null); setContactForm(EMPTY_CONTACT); }}
                                        className="inline-flex items-center gap-2 border border-ink/20 px-6 py-3 font-heading text-xs font-bold uppercase tracking-[0.14em] text-ink">
                                        <X size={14} /> Cancel
                                    </button>
                                )}
                            </div>
                        </form>

                        <ul className="grid gap-3" data-testid="admin-contacts-list">
                            {contacts.map((c) => (
                                <li key={c.id} className="flex flex-wrap items-center gap-x-6 gap-y-2 bg-white p-5 border border-gray-200">
                                    <div className="min-w-0 flex-1">
                                        <p className="font-heading text-[15px] font-bold text-ink">{c.department}</p>
                                        <p className="mt-0.5 text-sm text-gray-600">
                                            {[c.name, c.title].filter(Boolean).join(" — ") || "No name set"}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            {[c.email, c.phone].filter(Boolean).join(" · ")}
                                            {c.notes ? ` — ${c.notes}` : ""}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button type="button" aria-label={`Edit ${c.department}`} data-testid={`admin-contact-edit-${c.id}`}
                                            onClick={() => { setEditingContact(c.id); setContactForm({
                                                department: c.department || "", name: c.name || "", title: c.title || "",
                                                email: c.email || "", phone: c.phone || "", notes: c.notes || "",
                                            }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                            className="inline-flex h-9 w-9 items-center justify-center border border-ink/15 text-ink hover:border-gold hover:text-gold">
                                            <Pencil size={14} />
                                        </button>
                                        <button type="button" aria-label={`Delete ${c.department}`} data-testid={`admin-contact-delete-${c.id}`}
                                            onClick={() => removeContact(c.id)}
                                            className="inline-flex h-9 w-9 items-center justify-center border border-ink/15 text-ink hover:border-red-500 hover:text-red-600">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                            {contacts.length === 0 && (
                                <li className="bg-white p-6 text-sm text-gray-500 border border-gray-200">
                                    No contacts yet — add your first department above.
                                </li>
                            )}
                        </ul>
                    </div>
                )}

                {tab === "knowledge" && (
                    <div className="mt-8 grid gap-8">
                        <form onSubmit={saveKnowledge} data-testid="admin-knowledge-form" className="bg-white p-6 border border-gray-200">
                            <h2 className="font-heading text-lg font-bold text-ink">
                                {editingKnowledge ? "Edit Answer" : "Add Approved Answer"}
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Teach the assistant exactly how to answer a topic.
                            </p>
                            <input aria-label="Topic" placeholder="Topic * (e.g. Detention policy)" required value={knowledgeForm.topic}
                                onChange={(e) => setKnowledgeForm((f) => ({ ...f, topic: e.target.value }))}
                                data-testid="admin-knowledge-topic" className={`${inputClass} mt-5`} />
                            <textarea aria-label="Answer content" placeholder="The exact answer the assistant should give…" required rows={4} value={knowledgeForm.content}
                                onChange={(e) => setKnowledgeForm((f) => ({ ...f, content: e.target.value }))}
                                data-testid="admin-knowledge-content" className={`${inputClass} mt-4 resize-y`} />
                            <div className="mt-5 flex gap-3">
                                <button type="submit" data-testid="admin-knowledge-save"
                                    className="inline-flex items-center gap-2 bg-gold px-6 py-3 font-heading text-xs font-bold uppercase tracking-[0.14em] text-ink hover:bg-gold-light">
                                    <Plus size={14} /> {editingKnowledge ? "Save Changes" : "Add Answer"}
                                </button>
                                {editingKnowledge && (
                                    <button type="button" data-testid="admin-knowledge-cancel"
                                        onClick={() => { setEditingKnowledge(null); setKnowledgeForm(EMPTY_KNOWLEDGE); }}
                                        className="inline-flex items-center gap-2 border border-ink/20 px-6 py-3 font-heading text-xs font-bold uppercase tracking-[0.14em] text-ink">
                                        <X size={14} /> Cancel
                                    </button>
                                )}
                            </div>
                        </form>

                        <ul className="grid gap-3" data-testid="admin-knowledge-list">
                            {knowledge.map((k) => (
                                <li key={k.id} className="flex flex-wrap items-start gap-x-6 gap-y-2 bg-white p-5 border border-gray-200">
                                    <div className="min-w-0 flex-1">
                                        <p className="font-heading text-[15px] font-bold text-ink">{k.topic}</p>
                                        <p className="mt-1 text-sm leading-relaxed text-gray-600">{k.content}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button type="button" aria-label={`Edit ${k.topic}`} data-testid={`admin-knowledge-edit-${k.id}`}
                                            onClick={() => { setEditingKnowledge(k.id); setKnowledgeForm({ topic: k.topic, content: k.content }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                            className="inline-flex h-9 w-9 items-center justify-center border border-ink/15 text-ink hover:border-gold hover:text-gold">
                                            <Pencil size={14} />
                                        </button>
                                        <button type="button" aria-label={`Delete ${k.topic}`} data-testid={`admin-knowledge-delete-${k.id}`}
                                            onClick={() => removeKnowledge(k.id)}
                                            className="inline-flex h-9 w-9 items-center justify-center border border-ink/15 text-ink hover:border-red-500 hover:text-red-600">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
